import type { TrackedCategoryAssignment, TrackedCategorySplit } from "./models.js";

type TrackedCategoryFields = {
  amount: number;
  trackedCategorySplits?: TrackedCategorySplit[];
};

const SPLIT_AMOUNT_TOLERANCE = 0.01;

const round2 = (value: number) => Math.round(value * 100) / 100;

export const getTrackedCategorySplitsTotal = (splits: TrackedCategorySplit[]) =>
  splits.reduce((sum, split) => sum + split.amount, 0);

/** Scale stored split magnitudes to `|transactionAmount|` when sync drifts them. */
const scaleSplitsToTransactionAmount = (
  splits: TrackedCategorySplit[],
  transactionAmount: number,
): TrackedCategorySplit[] => {
  const target = Math.abs(transactionAmount);
  const total = getTrackedCategorySplitsTotal(splits);
  if (total <= 0 || Math.abs(total - target) <= SPLIT_AMOUNT_TOLERANCE) {
    return splits;
  }

  const scale = target / total;
  const scaled = splits.map((split) => ({
    trackedCategoryId: split.trackedCategoryId,
    amount: round2(split.amount * scale),
  }));

  // Put rounding residue on the last line so the scaled set stays balanced.
  const scaledTotal = getTrackedCategorySplitsTotal(scaled);
  const last = scaled[scaled.length - 1];
  if (last) {
    last.amount = round2(last.amount + (target - scaledTotal));
  }
  return scaled;
};

/**
 * Canonical categorization mode for a transaction.
 * Storage is always `trackedCategorySplits`: length 1 → single, length ≥ 2 → split.
 */
export const getTrackedCategoryAssignment = (
  transaction: TrackedCategoryFields,
): TrackedCategoryAssignment => {
  const splits = transaction.trackedCategorySplits;
  if (splits && splits.length >= 2) {
    return {
      mode: "split",
      splits: scaleSplitsToTransactionAmount(splits, transaction.amount),
    };
  }

  const onlySplit = splits?.length === 1 ? splits[0] : undefined;
  if (onlySplit) {
    return {
      mode: "single",
      trackedCategoryId: onlySplit.trackedCategoryId,
      amount: Math.abs(transaction.amount),
    };
  }

  return { mode: "none" };
};

/** Flat positive-magnitude allocations derived from the canonical assignment. */
export const getTrackedCategoryAllocations = (
  transaction: TrackedCategoryFields,
): TrackedCategorySplit[] => {
  const assignment = getTrackedCategoryAssignment(transaction);
  switch (assignment.mode) {
    case "none":
      return [];
    case "single":
      return [
        {
          trackedCategoryId: assignment.trackedCategoryId,
          amount: assignment.amount,
        },
      ];
    case "split":
      return assignment.splits;
  }
};

/**
 * Allocations signed like `transaction.amount` for budget/API netting.
 * Stored split magnitudes stay positive; this applies the transaction sign so
 * refunds/reversals reduce category totals the same way single-category did.
 */
export const getTrackedCategorySignedAllocations = (
  transaction: TrackedCategoryFields,
): TrackedCategorySplit[] => {
  const sign = transaction.amount === 0 ? 1 : Math.sign(transaction.amount);
  return getTrackedCategoryAllocations(transaction).map((allocation) => ({
    trackedCategoryId: allocation.trackedCategoryId,
    amount: sign * allocation.amount,
  }));
};

export const areTrackedCategorySplitsBalanced = (
  splits: TrackedCategorySplit[],
  transactionAmount: number,
) =>
  Math.abs(getTrackedCategorySplitsTotal(splits) - Math.abs(transactionAmount)) <=
  SPLIT_AMOUNT_TOLERANCE;

export type TrackedCategorySplitsValidation =
  | { ok: true }
  | {
      ok: false;
      reason: "tooFew" | "invalidLine" | "unknownCategory" | "duplicateCategory" | "unbalanced";
    };

export const validateTrackedCategorySplits = (
  splits: TrackedCategorySplit[],
  options: {
    transactionAmount: number;
    activeCategoryIds?: ReadonlySet<string> | readonly string[];
  },
): TrackedCategorySplitsValidation => {
  if (splits.length < 2) {
    return { ok: false, reason: "tooFew" };
  }

  const activeIds = options.activeCategoryIds
    ? options.activeCategoryIds instanceof Set
      ? options.activeCategoryIds
      : new Set(options.activeCategoryIds)
    : undefined;

  const seenCategoryIds = new Set<string>();
  for (const split of splits) {
    if (!split.trackedCategoryId || Number.isNaN(split.amount) || split.amount <= 0) {
      return { ok: false, reason: "invalidLine" };
    }
    if (activeIds && !activeIds.has(split.trackedCategoryId)) {
      return { ok: false, reason: "unknownCategory" };
    }
    if (seenCategoryIds.has(split.trackedCategoryId)) {
      return { ok: false, reason: "duplicateCategory" };
    }
    seenCategoryIds.add(split.trackedCategoryId);
  }

  if (!areTrackedCategorySplitsBalanced(splits, options.transactionAmount)) {
    return { ok: false, reason: "unbalanced" };
  }

  return { ok: true };
};

export type TransferBudgetOutcome = "unsettled" | "booked" | "failed";

export const isFailedSwanTransactionStatus = (
  status:
    | "Booked"
    | "Deferred"
    | "Rejected"
    | "Pending"
    | "Canceled"
    | "Upcoming"
    | "Released"
    | "Settled"
    | undefined,
) => {
  switch (status) {
    case "Rejected":
    case "Canceled":
      return true;
    case "Booked":
    case "Deferred":
    case "Pending":
    case "Upcoming":
    case "Released":
    case "Settled":
    case undefined:
      return false;
  }
};

/**
 * Positive amount a transfer request adds to budget réalisé.
 * Accepted and completed requests count until a booked transaction exists.
 * A failed payout, or a request already covered by that transaction, adds nothing.
 */
export const realizedAmountFromTransferRequest = (
  request: {
    status: "pending" | "completed" | "accepted" | "rejected" | "cancelled";
    amount: string;
    trackedCategoryId?: string;
  },
  outcome: TransferBudgetOutcome,
) => {
  if (!request.trackedCategoryId) return 0;
  const amount = parseFloat(request.amount);
  if (!Number.isFinite(amount)) return 0;

  let completedAmount = 0;
  switch (outcome) {
    case "unsettled":
      completedAmount = amount;
      break;
    case "booked":
    case "failed":
      break;
  }

  switch (request.status) {
    case "accepted":
    case "completed":
      return completedAmount;
    case "pending":
    case "rejected":
    case "cancelled":
      return 0;
  }
};
